/**
 * RBAC constants for Medplum-backed role and permission resolution.
 *
 * Extension URLs and policy IDs are sourced from medplum-server `fhir-model`
 * (single source of truth). UI-specific names are preserved for callers.
 */

import {
  ATTENDANCE_STATUS_EXT,
  EXT_URLS,
  IOP_PARENT_ENCOUNTER_EXT,
  THERAPY_SERVICE_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import { POLICY_IDS as FHIR_POLICY_IDS } from '@unify/fhir-model/structural-ids';

// ---------------------------------------------------------------------------
// FHIR extension URLs (aligned with medplum-server/src/fhir-model/constants)
// ---------------------------------------------------------------------------

export const EXTENSION_URLS = {
  /** Role slug stored on Practitioner/Patient/RelatedPerson profile. */
  role: EXT_URLS.role,
  /** Resolved UI-permission JSON stored on the profile by a Bot/seed. */
  uiPermissions: EXT_URLS.uiPermissions,
  /** Raw per-user permission overrides (sparse object, merged over role defaults). */
  uiPermissionOverrides: EXT_URLS.uiPermissionOverrides,
  /** Guarantor/proxy login: primary Patient whose portal data to load (Patient reference). */
  portalSubjectPatient: EXT_URLS.portalSubjectPatient,
  /** Client+guarantor: additional Patient(s) this user can view in the portal (repeatable). */
  portalRelatedPatient: EXT_URLS.portalRelatedPatient,
  /** IOP: child Encounter → parent Encounter reference for daily rollup billing. */
  iopParentEncounter: IOP_PARENT_ENCOUNTER_EXT,
  /** Attendance status on Appointment.participant (extended beyond FHIR base). */
  attendanceStatus: ATTENDANCE_STATUS_EXT,
  /** Therapy service type CodeSystem URI (coding.system on Appointment/Encounter). */
  therapyServiceType: THERAPY_SERVICE_TYPE_SYSTEM,
} as const;

// ---------------------------------------------------------------------------
// AccessPolicy resource IDs (pilot seed — must match structural-ids.ts)
// ---------------------------------------------------------------------------

export const POLICY_IDS = {
  /** Patient can see only their own data. */
  patientSelfService: FHIR_POLICY_IDS.patientSelf,
  /** Provider: CareTeam compartment + org. */
  providerScoped: FHIR_POLICY_IDS.provider,
  /** Supervisor: read-heavy CareTeam scoped policy. */
  supervisorScoped: FHIR_POLICY_IDS.supervisor,
  /**
   * Unify Admin: project-wide allow-list for platform visibility and admin configuration; no clinical authoring, billing, messaging, or group therapy.
   */
  unifyAdmin: FHIR_POLICY_IDS.unifyAdmin,
  /**
   * Practice Owner / Practice Admin: access scoped to their Organization.
   */
  practiceAdmin: FHIR_POLICY_IDS.practiceAdmin,
  practiceOwner: FHIR_POLICY_IDS.practiceOwner,
} as const;

// ---------------------------------------------------------------------------
// Profile resource type helpers
// ---------------------------------------------------------------------------

export type MedplumProfileType = 'Patient' | 'Practitioner' | 'RelatedPerson';

export function isMedplumProfileType(rt: string | undefined): rt is MedplumProfileType {
  return rt === 'Patient' || rt === 'Practitioner' || rt === 'RelatedPerson';
}
